
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TerminalMarketPrice {
  commodity_name: string;
  variety: string | null;
  group_name: string | null;
  current_price: number;
  unit: string;
  market_type: string;
  report_date: string;
  market_city: string;
}

export const useTerminalMarketPrices = () => {
  const [terminalMarketPrices, setTerminalMarketPrices] = useState<TerminalMarketPrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTerminalMarketPricesFromUserCrops = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🌾 Fetching Terminal Market prices based on user crop categories...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found for terminal market prices');
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, location")
        .eq("user_id", user.id)
        .single();

      if (!profile) {
        console.log('No profile found');
        return;
      }

      const { data: userCrops } = await supabase
        .from("user_crop_categories")
        .select("commodity, variety, group_name")
        .eq("user_id", profile.id);

      if (!userCrops || userCrops.length === 0) {
        console.log('No user crop categories found');
        setTerminalMarketPrices([]);
        return;
      }

      console.log(`Found ${userCrops.length} user crop categories`);

      const commodities = [...new Set(userCrops.map(crop => crop.commodity).filter(Boolean))];
      const varieties = [...new Set(userCrops.map(crop => crop.variety).filter(Boolean))];
      const groupNames = [...new Set(userCrops.map(crop => crop.group_name).filter(Boolean))];

      console.log('User crop data to fetch terminal market prices for:', {
        commodities,
        varieties,
        groupNames,
        userLocation: profile.location
      });

      const { data: priceResponse, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          fetchTerminalMarketData: true,
          userLocation: profile.location,
          commodities: commodities,
          varieties: varieties,
          groupNames: groupNames,
          targetReportType: 'terminal market prices'
        }
      });

      if (error) {
        console.error('Error fetching terminal market prices from user crops:', error);
        return;
      }

      if (priceResponse?.success && priceResponse.data) {
        console.log(`💰 Received ${priceResponse.data.length} Terminal Market price records based on user crop categories`);
        setTerminalMarketPrices(priceResponse.data);
      } else {
        console.log('No terminal market price data received');
        setTerminalMarketPrices([]);
      }
    } catch (err) {
      console.error("Error in fetchTerminalMarketPricesFromUserCrops:", err);
      setError('Failed to fetch terminal market prices');
    } finally {
      setLoading(false);
    }
  };

  const fetchAndStoreProfilePrices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Triggering profile price fetching and storage...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found for profile price storage');
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, location")
        .eq("user_id", user.id)
        .single();

      if (!profile) {
        console.log('No profile found');
        return;
      }

      const { data: userCrops } = await supabase
        .from("user_crop_categories")
        .select("commodity, variety, group_name")
        .eq("user_id", profile.id);

      const commodities = userCrops ? [...new Set(userCrops.map(c => c.commodity).filter(Boolean))] : [];
      const varieties = userCrops ? [...new Set(userCrops.map(c => c.variety).filter(Boolean))] : [];

      console.log('Triggering profile price fetch and store for:', { commodities, varieties });

      const { data: response, error } = await supabase.functions.invoke('fetch-usda-data', {
        body: { 
          fetchAndStoreProfilePrices: true,
          userLocation: profile.location,
          commodities: commodities,
          varieties: varieties
        }
      });

      if (error) {
        console.error('Error fetching and storing profile prices:', error);
        return;
      }

      if (response?.success) {
        console.log(`✅ Successfully triggered profile price fetch and store: ${response.message}`);
        return response.data;
      }
    } catch (err) {
      console.error("Error in fetchAndStoreProfilePrices:", err);
      setError('Failed to fetch and store profile prices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerminalMarketPricesFromUserCrops();
  }, []);

  return {
    terminalMarketPrices,
    loading,
    error,
    fetchTerminalMarketPricesFromUserCrops,
    fetchAndStoreProfilePrices
  };
};
